import { MutableRefObject } from "react";
import { BrandEnum } from "../_common/enum/brand.enum";
import { TrackingStageEnum } from "../_common/enum/trackingStage.enum";
import { IQuoteConversionAuditManage } from "../_common/interface/quoteConversionAudit/iQuoteConversionAudit.interface";
import { IQuoteTrackingDetail } from "../_common/interface/quoteTrackingDetail/iQuoteTrackingDetail.interface";
import { quoteConversionAuditService } from "../_services/quoteConversionAudit.service";
import { quoteTracking } from "../_services/quoteTracking.service";
import { inferAttributionFromQueryParams, normalizeTrackingParam } from "./attribution.helper";
import { getClientEnvironment } from "./clientEnviroment.helper";
import {
    createMessageId,
    getTrackingParamsForFormSubmit,
    hasConversionBeenSent,
    markConversionAsSent,
} from "./trackingSnapshot.helper";

interface INotifyParentConversionWithAuditParams {
    formId?: string | number | null;
    quoteId?: number | null;
    brandId: BrandEnum | null;
    formTypeId?: string | null;
    activeStep?: number | null;
    eventName?: string | null;
    pageUrl?: string | null;
    sentRef?: MutableRefObject<boolean>;
    parentWindow?: Window | null;
    saveQuoteSubmittedTracking?: boolean;
    queryParams?: URLSearchParams | null;
}

/** Meta fields for parent-page pixel (Advanced Matching). Names match WordPress listener. */
export interface IParentConversionMetaParams {
    fbclid: string;
    fbc: string;
    fbp: string;
}

export function getMetaParamsForParentPostMessage(
    queryParams: URLSearchParams
): IParentConversionMetaParams {
    const fbclid = normalizeTrackingParam(queryParams.get("fbclid"));
    let fbc =
        normalizeTrackingParam(queryParams.get("_fbc")) ||
        normalizeTrackingParam(queryParams.get("fbc"));
    const fbp =
        normalizeTrackingParam(queryParams.get("_fbp")) ||
        normalizeTrackingParam(queryParams.get("fbp"));

    if (!fbc && fbclid) {
        fbc = `fb.1.${Date.now()}.${fbclid}`;
    }

    return { fbclid, fbc, fbp };
}

function buildQuoteTrackingDetailInline(params: {
    formId: number;
    brandId: BrandEnum | null;
    formTypeId?: string | null;
    quoteId?: number | null;
    trackingStageId: TrackingStageEnum;
    queryParams: URLSearchParams;
    visitId: string;
}): IQuoteTrackingDetail {
    const qp = params.queryParams;
    const attribution = inferAttributionFromQueryParams(qp);

    return {
        formId: params.formId,
        brandId: params.brandId,
        formTypeId: params.formTypeId ?? "",
        quoteId: params.quoteId != null ? String(params.quoteId) : "",
        trackingStageId: params.trackingStageId,
        source: attribution.source,
        originalSource: attribution.originalSource,
        medium: attribution.medium,
        originalMedium: attribution.originalMedium,
        campaign: attribution.campaign,
        originalCampaign: attribution.originalCampaign,
        keyword: attribution.keyword,
        originalKeyword: attribution.originalKeyword,
        ip: normalizeTrackingParam(qp.get("handl_ip")),
        gClId: attribution.gclid,
        handlLandingPage: attribution.handlLandingPage,
        handlLandingPageBase: normalizeTrackingParam(qp.get("handl_landing_page_base")),
        handlRef: normalizeTrackingParam(qp.get("handl_ref")),
        handlOriginalRef: attribution.handlOriginalRef,
        handlRefDomain: attribution.handlRefDomain,
        handlUrl: normalizeTrackingParam(qp.get("handl_url")),
        handlUrlBase: normalizeTrackingParam(qp.get("handl_url_base")),
        handlID: normalizeTrackingParam(qp.get("handlID")),
        trafficSource: attribution.trafficSource,
        originalReferringUrl: "",
        metaFbc: attribution.fbc,
        metaFbp: attribution.fbp,
        metaFbclid: attribution.fbclid,
        isFormDuplicate: false,
        formSequenceNo: null,
        visitId: params.visitId,
    };
}

export async function notifyParentConversionWithAudit(
    params: INotifyParentConversionWithAuditParams
): Promise<void> {
    const numericFormId = params.formId ? Number(params.formId) : null;
    const transactionId = String(params.formId || "");
    if (!transactionId || !numericFormId) return;

    const eventName = params.eventName || "quote_form_submission";
    const messageId = createMessageId();
    const env = getClientEnvironment();

    const { params: trackingParams, visitId } = getTrackingParamsForFormSubmit(numericFormId);
    const metaForParent = getMetaParamsForParentPostMessage(trackingParams);

    const auditBase: IQuoteConversionAuditManage = {
        formId: numericFormId,
        quoteId: params.quoteId ?? null,
        brandId: params.brandId ?? null,
        visitId,
        eventName,
        transactionId,
        messageId,
        activeStep: params.activeStep ?? null,
        pageUrl: params.pageUrl || window.location.href,
        deviceType: env.deviceType,
        browser: env.browser,
        operatingSystem: env.operatingSystem,
        userAgent: env.userAgent,
    };

    if (params.saveQuoteSubmittedTracking) {
        try {
            const trackingPayload = buildQuoteTrackingDetailInline({
                formId: numericFormId,
                brandId: params.brandId,
                formTypeId: params.formTypeId ?? "",
                quoteId: params.quoteId ?? null,
                trackingStageId: TrackingStageEnum.QuoteSubmitted,
                queryParams: trackingParams,
                visitId,
            });

            await quoteTracking.post(trackingPayload);
        } catch {
        }
    }

    const alreadySent =
        !!params.sentRef?.current || hasConversionBeenSent(transactionId);

    if (alreadySent) {
        try {
            await quoteConversionAuditService.post({
                ...auditBase,
                stage: "child_send_blocked",
                isDuplicate: true,
            });
        } catch (err) {
            console.warn("[QuoteConversionAudit] Failed to save child_send_blocked", err);
        }
        return;
    }

    if (params.sentRef) {
        params.sentRef.current = true;
    }

    markConversionAsSent(transactionId);

    try {
        await quoteConversionAuditService.post({
            ...auditBase,
            stage: "child_send_attempt",
            isDuplicate: false,
        });
    } catch (err) {
        console.warn("[QuoteConversionAudit] Failed to save child_send_attempt", err);
    }

    const targetWindow = params.parentWindow || window.parent;
    if (!targetWindow) return;

    targetWindow.postMessage(
        {
            type: "conversion",
            event: eventName,
            transaction_id: transactionId,
            message_id: messageId,
            fbclid: metaForParent.fbclid,
            fbc: metaForParent.fbc,
            fbp: metaForParent.fbp,
        },
        "*"
    );
}